import React, { useMemo } from 'react';
import {
    Text,
    ActionIcon,
    Flex,
    Box,
    Image,
    Badge,
    Group,
    ScrollArea,
    Tooltip,
} from '@mantine/core';
import { Dropzone, FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import {
    IconEye,
    IconX,
    IconCirclePlus,
    IconAlertCircle,
    IconArrowBackUp,
} from '@tabler/icons-react';
import { When } from '../common';
import SuperAdminS3Path from '../common/SuperAdminS3Path';
import FileIcons from '../../assets/file-icons';
import EmptyListIllustration from '../../assets/illustrations/empty-list.png';

export interface ProofFile {
    id?: string;
    fileName: string;
    fileSize?: number;
    fileType?: string;
    downloadUrl?: string;
    documentS3Path?: string;
    file?: File;
    isNew?: boolean;
    /** Existing server proof marked for deletion on next save */
    pendingRemove?: boolean;
}

function formatFileSize(bytes?: number): string {
    if (bytes == null || bytes <= 0) return '';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) {
        return `${mb.toFixed(1)} MB`;
    }
    const kb = bytes / 1024;
    return `${kb.toFixed(1)} KB`;
}

interface SurveyProofUploadProps {
    proofs: ProofFile[];
    onAddProofs: (files: File[]) => void;
    onRemoveProof: (proof: ProofFile) => void;
    onViewProof: (proof: ProofFile) => void;
    isUploading?: boolean;
    disabled?: boolean;
}

const ALLOWED_MIME_TYPES = [
    MIME_TYPES.jpeg,
    MIME_TYPES.png,
    MIME_TYPES.gif,
    MIME_TYPES.webp,
    MIME_TYPES.pdf,
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function SurveyProofUpload({
    proofs,
    onAddProofs,
    onRemoveProof,
    onViewProof,
    isUploading = false,
    disabled = false,
}: SurveyProofUploadProps) {
    const handleDrop = (files: FileWithPath[]) => {
        if (files.length > 0) {
            onAddProofs(files);
        }
    };

    const proofDisplayData = useMemo(
        () =>
            proofs.map(proof => {
                const extension =
                    proof.fileName.split('.').pop()?.toUpperCase() || 'UNK';
                const fileIcon =
                    FileIcons[extension as keyof typeof FileIcons] ??
                    FileIcons.UNK;
                return { proof, extension, fileIcon };
            }),
        [proofs],
    );

    const newCount = useMemo(
        () => proofs.filter(p => p.isNew).length,
        [proofs],
    );
    const removedCount = useMemo(
        () => proofs.filter(p => p.pendingRemove).length,
        [proofs],
    );
    const hasUnsavedChanges = newCount > 0 || removedCount > 0;

    const unsavedBannerText = useMemo(() => {
        const parts: string[] = [];
        if (newCount > 0) {
            parts.push(
                `${newCount} new file${newCount === 1 ? '' : 's'} need${
                    newCount === 1 ? 's' : ''
                } to be saved`,
            );
        }
        if (removedCount > 0) {
            parts.push(
                `${removedCount} file${
                    removedCount === 1 ? '' : 's'
                } marked for removal`,
            );
        }
        if (parts.length === 0) return '';
        const detail = parts.length === 1 ? parts[0] : parts.join(' and ');
        return `${detail}. Tap Update to confirm.`;
    }, [newCount, removedCount]);

    return (
        <Box className="bg-white rounded-lg p-4 shadow-sm flex flex-col min-h-0">
            <Group
                justify="space-between"
                align="center"
                className="mb-3"
                wrap="nowrap"
            >
                <Text fw={600} size="sm">
                    Proof
                </Text>
                <When condition={proofs.length > 0}>
                    <Badge
                        size="sm"
                        variant="light"
                        color="gray"
                        className="normal-case font-medium"
                    >
                        {proofs.length} File{proofs.length === 1 ? '' : 's'}
                    </Badge>
                </When>
            </Group>

            <When condition={hasUnsavedChanges && !disabled}>
                <Flex
                    align="center"
                    gap={10}
                    className="mb-3 rounded-md border border-orange-100/90 bg-[#fff8f3] px-3 py-2.5"
                >
                    <IconAlertCircle
                        size={20}
                        className="text-orange-400 flex-shrink-0"
                        aria-hidden
                    />
                    <div className="min-w-0">
                        <Text size="sm" fw={600} className="text-orange-900/75">
                            You have unsaved changes
                        </Text>
                        <Text size="sm" className="text-orange-800/70 mt-0.5">
                            {unsavedBannerText}
                        </Text>
                    </div>
                </Flex>
            </When>

            {proofDisplayData.length === 0 ? (
                <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    className="py-2"
                >
                    <Image
                        src={EmptyListIllustration}
                        alt="No attachments"
                        w={120}
                        h={120}
                        fit="contain"
                        className="mb-1"
                    />
                    <Text size="sm" c="dimmed" className="py-1 text-center">
                        No attachments uploaded for this competitor
                    </Text>
                </Flex>
            ) : (
                <ScrollArea
                    type="auto"
                    offsetScrollbars
                    classNames={{
                        root: 'max-h-[min(42vh,280px)] min-h-0',
                        viewport: 'max-h-[min(42vh,280px)]',
                    }}
                >
                    <div className="space-y-2 pr-1 pb-0.5">
                        {proofDisplayData.map(
                            ({ proof, extension, fileIcon }, index) => {
                                const isPendingAdd = Boolean(proof.isNew);
                                const isPendingRemove = Boolean(
                                    proof.pendingRemove,
                                );
                                const isSaved =
                                    !isPendingAdd && !isPendingRemove;

                                const sizeLabel = formatFileSize(
                                    proof.fileSize ??
                                        (proof.file
                                            ? proof.file.size
                                            : undefined),
                                );

                                return (
                                    <div
                                        key={proof.id || `new-${index}`}
                                        className="relative rounded-md border border-gray-200 bg-white shadow-sm overflow-hidden"
                                    >
                                        <When condition={isPendingAdd}>
                                            <Box
                                                aria-hidden
                                                className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-orange-400"
                                            />
                                        </When>
                                        <When condition={isPendingRemove}>
                                            <Box
                                                aria-hidden
                                                className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-red-400"
                                            />
                                        </When>
                                        <Flex
                                            align="center"
                                            justify="space-between"
                                            className={`py-2.5 pr-3 ${
                                                isPendingAdd || isPendingRemove
                                                    ? 'pl-[11px]'
                                                    : 'pl-3'
                                            }`}
                                        >
                                            <Flex
                                                align="center"
                                                gap={10}
                                                className="flex-1 min-w-0"
                                            >
                                                <Image
                                                    w={20}
                                                    h={20}
                                                    src={fileIcon}
                                                    alt={`${extension} Icon`}
                                                    fit="contain"
                                                    className={
                                                        isPendingRemove
                                                            ? 'opacity-60'
                                                            : undefined
                                                    }
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <Text
                                                        size="sm"
                                                        className={`truncate font-medium ${
                                                            isPendingRemove
                                                                ? 'text-gray-500 line-through'
                                                                : 'text-gray-700'
                                                        }`}
                                                        title={proof.fileName}
                                                    >
                                                        {proof.fileName}
                                                    </Text>
                                                    <When
                                                        condition={Boolean(
                                                            sizeLabel,
                                                        )}
                                                    >
                                                        <Text
                                                            size="xs"
                                                            c="dimmed"
                                                            className="mt-0.5"
                                                        >
                                                            {sizeLabel}
                                                        </Text>
                                                    </When>
                                                    <Group
                                                        gap={6}
                                                        wrap="wrap"
                                                        className="mt-1.5"
                                                    >
                                                        <When
                                                            condition={isSaved}
                                                        >
                                                            <Badge
                                                                size="xs"
                                                                variant="light"
                                                                color="green"
                                                                className="normal-case font-medium"
                                                            >
                                                                Saved
                                                            </Badge>
                                                        </When>
                                                        <When
                                                            condition={
                                                                isPendingAdd
                                                            }
                                                        >
                                                            <Badge
                                                                size="xs"
                                                                variant="light"
                                                                color="orange"
                                                                className="normal-case font-medium"
                                                            >
                                                                New
                                                            </Badge>
                                                            <Badge
                                                                size="xs"
                                                                variant="light"
                                                                color="orange"
                                                                className="normal-case font-medium"
                                                            >
                                                                Unsaved
                                                            </Badge>
                                                        </When>
                                                        <When
                                                            condition={
                                                                isPendingRemove
                                                            }
                                                        >
                                                            <Badge
                                                                size="xs"
                                                                variant="light"
                                                                color="red"
                                                                className="normal-case font-medium"
                                                            >
                                                                Removed
                                                            </Badge>
                                                        </When>
                                                    </Group>
                                                </div>
                                            </Flex>
                                            <Flex align="center" gap={6}>
                                                <SuperAdminS3Path
                                                    path={proof.documentS3Path}
                                                    bucket="documents"
                                                />
                                                <When
                                                    condition={Boolean(
                                                        proof.downloadUrl ||
                                                            proof.file,
                                                    )}
                                                >
                                                    <ActionIcon
                                                        variant="subtle"
                                                        size="sm"
                                                        className="text-blue-600 hover:bg-blue-50"
                                                        onClick={() =>
                                                            onViewProof(proof)
                                                        }
                                                        title="View file"
                                                        aria-label="View file"
                                                    >
                                                        <IconEye size={18} />
                                                    </ActionIcon>
                                                </When>
                                                <When
                                                    condition={
                                                        !disabled &&
                                                        !isPendingRemove
                                                    }
                                                >
                                                    <ActionIcon
                                                        variant="subtle"
                                                        size="sm"
                                                        className="text-gray-400 hover:text-red-500 hover:bg-red-50"
                                                        onClick={() =>
                                                            onRemoveProof(proof)
                                                        }
                                                        title="Remove file"
                                                        aria-label="Remove file"
                                                        disabled={isUploading}
                                                    >
                                                        <IconX size={18} />
                                                    </ActionIcon>
                                                </When>
                                                <When
                                                    condition={
                                                        !disabled &&
                                                        isPendingRemove
                                                    }
                                                >
                                                    <Tooltip
                                                        label="Undo"
                                                        withArrow
                                                        openDelay={300}
                                                    >
                                                        <ActionIcon
                                                            variant="subtle"
                                                            size="sm"
                                                            color="primary"
                                                            onClick={() =>
                                                                onRemoveProof(
                                                                    proof,
                                                                )
                                                            }
                                                            disabled={
                                                                isUploading
                                                            }
                                                            aria-label="Undo"
                                                        >
                                                            <IconArrowBackUp
                                                                size={18}
                                                                aria-hidden
                                                            />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                </When>
                                            </Flex>
                                        </Flex>
                                    </div>
                                );
                            },
                        )}
                    </div>
                </ScrollArea>
            )}

            <When condition={!disabled}>
                <Dropzone
                    onDrop={handleDrop}
                    onReject={files => {
                        files.forEach(({ file, errors }) => {
                            errors.forEach(error => {
                                console.warn(
                                    `${file.name}: ${error.code} - ${error.message}`,
                                );
                            });
                        });
                    }}
                    maxSize={MAX_FILE_SIZE}
                    accept={ALLOWED_MIME_TYPES}
                    disabled={isUploading}
                    className="mt-3"
                    classNames={{
                        root: 'border-none bg-transparent p-0',
                    }}
                >
                    <Flex
                        align="center"
                        gap={8}
                        className="text-[#003B7E] hover:text-[#002F66] w-fit cursor-pointer"
                    >
                        <Text size="sm" fw={600}>
                            Upload Proof
                        </Text>
                        <IconCirclePlus size={20} stroke={2} />
                    </Flex>
                </Dropzone>
            </When>
        </Box>
    );
}

export default SurveyProofUpload;
